
















































































import Vue from 'vue';
import TitleProvider from '@/api/TitleProvider';
import ITitle from '@/models/title/ITitle';
import TitleListEntry from './TitleListEntry.vue';
import DvBrowseFrame from '@/components/util/DvBrowseFrame.vue';
import ResponsiveCardList from '@/components/util/ResponsiveCardList.vue';
import ResponsiveCardListEntry from '@/components/util/ResponsiveCardListEntry.vue';
import { SortDir } from '@/util/browseQuery';

//  Mirrors DvBrowseFrame.vue's own (unimported) BrowseState/ISortOption --
//  see ItemSearchEx.vue for why these are duplicated locally rather than
//  imported from the *.vue ambient shim.
type BrowseState = 'prompt' | 'loading' | 'results' | 'empty' | 'error' | 'end';

//  Only the two sort keys this page actually offers -- a subset of
//  browseQuery's SortKey ('relevance' | 'name' | 'id'), which also carries
//  'relevance' for item search's ranked results. Titles have no relevance
//  score, so it's never offered here.
type TitleSortKey = 'id' | 'name';

interface ISortOption {
    key: TitleSortKey;
    label: string;
}

interface IData {
    titles: ITitle[];
    loading: boolean;
    error: string;
    sort: TitleSortKey;
    asc: boolean;
    search: string;
}

export default Vue.extend({
    components: {
        TitleListEntry,
        DvBrowseFrame,
        ResponsiveCardList,
        ResponsiveCardListEntry,
    },
    data(): IData {
        return {
            titles: [],
            loading: true,
            error: '',
            sort: 'id',
            asc: true,
            search: '',
        };
    },
    mounted() {
        this.load();
    },
    computed: {
        sorts(): ISortOption[] {
            return [
                { key: 'id', label: 'ID' },
                { key: 'name', label: 'Name' },
            ];
        },
        dir(): SortDir {
            return this.asc ? 'asc' : 'desc';
        },
        //  Titles are fetched once and filtered/sorted entirely client-side
        //  (no pagination), so the frame's state is derived rather than
        //  driven by request lifecycle events like ItemSearchEx's.
        //
        //  The frame's own 'error' branch (DvError, full-screen, blocks the
        //  list) is reserved for TOTAL failure -- zero titles loaded. A
        //  failure partway through the paginated load still leaves earlier
        //  pages in `titles`, and that partial data stays genuinely useful,
        //  so it's surfaced instead as a non-blocking banner inside the
        //  results template (see above) while `state` proceeds to
        //  'empty'/'results' as if nothing had gone wrong. This mirrors the
        //  pre-adoption page, which kept the list rendered under a
        //  "some or all" warning toast on any load error.
        state(): BrowseState {
            if (this.loading) {
                return 'loading';
            }
            if (this.error && this.titles.length === 0) {
                return 'error';
            }
            return this.sorted.length === 0 ? 'empty' : 'results';
        },
        sorted(): ITitle[] {
            let sorter: (a: ITitle, b: ITitle) => number;
            switch (this.sort) {
                case 'id':
                    sorter = (a: ITitle, b: ITitle) => a.id - b.id;
                    break;
                case 'name':
                    sorter = (a: ITitle, b: ITitle) => a.name.message.localeCompare(b.name.message, undefined, {
                        sensitivity: 'base',
                        ignorePunctuation: true,
                    });
                    break;
                default:
                    throw new Error();
            }

            if (!this.asc) {
                const oldSorter = sorter;
                sorter = (a: ITitle, b: ITitle) => -oldSorter(a, b);
            }

            let r = [...this.titles].sort(sorter);

            if (this.search) {
                const s = this.search.toLowerCase();
                r = r.filter((v) => v.name.message.toLowerCase().includes(s));
            }

            return r;
        }
    },
    methods: {
        onInput(value: string) {
            this.search = value;
        },
        onSort(key: TitleSortKey) {
            this.sort = key;
        },
        onDir(dir: SortDir) {
            this.asc = dir === 'asc';
        },
        onRetry() {
            this.load();
        },
        async load() {
            try {
                this.loading = true;
                this.error = '';
                this.titles = [];
                let rr = [];
                let top = 0;
                do {
                    rr = await TitleProvider.listTitles(top);
                    if (rr.length > 0) {
                        top = rr[rr.length - 1].id + 1;
                    }

                    this.titles.push(...(rr.filter((v) => !v.name.message.startsWith('[MID:'))));
                } while (rr.length > 0)

            } catch (error) {
                this.error = String(error);
            } finally {
                this.loading = false;
            }
        },
    }
})
